import React, { useState, useMemo } from 'react';
import { 
  Mail, Send, FileText, Receipt, Search, RefreshCw, Loader2, 
  ChevronDown, ChevronUp, Users, Check, X, AlertCircle,
  Eye, MessageSquare, Clock, ExternalLink
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  deposit_paid: 'bg-blue-100 text-blue-700',
  fully_paid: 'bg-emerald-100 text-emerald-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
  refunded: 'bg-slate-100 text-slate-600'
};

const statusLabels = {
  pending: 'Pending',
  deposit_paid: 'Deposit Paid',
  fully_paid: 'Fully Paid',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  refunded: 'Refunded'
};

// Communication action card
const CommunicationCard = ({ icon: Icon, title, description, onClick, disabled, loading }) => (
  <div 
    className={cn(
      "bg-white border border-slate-200 rounded-lg p-4 transition-all",
      disabled ? "opacity-50 cursor-not-allowed" : "hover:border-violet-300 hover:shadow-sm cursor-pointer"
    )}
    onClick={disabled ? undefined : onClick}
  >
    <div className="flex items-start gap-3">
      <div className={cn(
        "w-10 h-10 rounded-lg flex items-center justify-center",
        disabled ? "bg-slate-100" : "bg-violet-100"
      )}>
        {loading ? (
          <Loader2 className="w-5 h-5 text-violet-600 animate-spin" />
        ) : (
          <Icon className={cn("w-5 h-5", disabled ? "text-slate-400" : "text-violet-600")} />
        )}
      </div>
      <div className="flex-1">
        <h4 className="font-medium text-slate-900">{title}</h4>
        <p className="text-sm text-slate-500 mt-0.5">{description}</p>
      </div>
    </div>
  </div>
);

export default function WeekenderCommunications({ 
  event, 
  bookings,
  loading: bookingsLoading, 
  authToken,
  onRefresh,
  onBookingSelect
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('customer');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Action states
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, booking: null });

  // Active bookings (exclude cancelled/refunded for communications)
  const activeBookings = useMemo(() => {
    return (bookings || []).filter(b => 
      b.status !== 'cancelled' && b.status !== 'refunded'
    );
  }, [bookings]);

  // Stats
  const stats = useMemo(() => {
    const pending = activeBookings.filter(b => b.status === 'pending').length;
    const depositPaid = activeBookings.filter(b => b.status === 'deposit_paid').length;
    const fullyPaid = activeBookings.filter(b => b.status === 'fully_paid' || b.status === 'confirmed').length;
    const withPayments = activeBookings.filter(b => (b.payment_records?.length || 0) > 0).length;
    
    return {
      total: activeBookings.length,
      pending,
      depositPaid,
      fullyPaid,
      withPayments
    };
  }, [activeBookings]);

  // Filter and sort bookings for the table
  const filteredBookings = useMemo(() => {
    let filtered = [...activeBookings];
    
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(b => 
        b.customer_name?.toLowerCase().includes(query) ||
        b.customer_email?.toLowerCase().includes(query) ||
        b.booking_id?.toLowerCase().includes(query)
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'customer':
          aVal = a.customer_name || '';
          bVal = b.customer_name || '';
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'date':
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
        default:
          aVal = a.booking_id;
          bVal = b.booking_id;
      }
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return filtered;
  }, [activeBookings, searchQuery, statusFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-3 h-3 ml-1" /> : 
      <ChevronDown className="w-3 h-3 ml-1" />;
  };

  // Communication handlers
  const handleSendConfirmation = async (booking) => {
    setSending(true);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/bookings/${booking.booking_id}/resend-confirmation`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        toast.success(`Confirmation email sent to ${booking.customer_email}`);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to send confirmation');
      }
    } catch (error) {
      toast.error('Failed to send confirmation email');
    } finally {
      setSending(false);
      setConfirmDialog({ open: false, type: null, booking: null });
    }
  };

  const handleSendReceipt = async (booking) => {
    if (!booking.payment_records || booking.payment_records.length === 0) {
      toast.error('No payments recorded for this booking');
      return;
    }
    
    setSending(true);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/bookings/${booking.booking_id}/send-receipt`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        toast.success(`Payment receipt sent to ${booking.customer_email}`);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to send receipt');
      }
    } catch (error) {
      toast.error('Failed to send payment receipt');
    } finally {
      setSending(false);
      setConfirmDialog({ open: false, type: null, booking: null });
    }
  };

  const handleSendInvoice = async (booking) => {
    setSending(true);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/bookings/${booking.booking_id}/send-invoice`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        toast.success(`Invoice sent to ${booking.customer_email}`);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to send invoice');
      }
    } catch (error) {
      toast.error('Failed to send invoice');
    } finally {
      setSending(false);
      setConfirmDialog({ open: false, type: null, booking: null });
    }
  };

  const openConfirmDialog = (type, booking) => {
    setConfirmDialog({ open: true, type, booking });
  };

  const executeAction = () => {
    if (!confirmDialog.booking) return;
    
    switch (confirmDialog.type) {
      case 'confirmation':
        handleSendConfirmation(confirmDialog.booking);
        break;
      case 'receipt':
        handleSendReceipt(confirmDialog.booking);
        break;
      case 'invoice':
        handleSendInvoice(confirmDialog.booking);
        break;
      default:
        break;
    }
  };

  const isLoading = bookingsLoading;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Communications</h2>
          <p className="text-sm text-slate-500">Send booking confirmations, receipts, and invoices</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefresh}
          disabled={isLoading}
          className="text-slate-500 hover:text-slate-700"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-1.5" />
          )}
          Refresh
        </Button>
      </div>

      {/* Communication Actions Overview */}
      <Card className="bg-gradient-to-br from-violet-50 to-white border-violet-100">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-4 h-4 text-violet-600" />
            Available Communications
          </CardTitle>
          <CardDescription>
            Select a booking from the list below to send communications
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CommunicationCard
              icon={Send}
              title="Booking Confirmation"
              description="Resend the original booking confirmation with all details"
              onClick={() => toast.info('Select a booking below to send confirmation')}
              disabled={false}
            />
            <CommunicationCard
              icon={Receipt}
              title="Payment Receipt"
              description="Send a receipt showing all payments and current balance"
              onClick={() => toast.info('Select a booking below to send receipt')}
              disabled={false}
            />
            <CommunicationCard
              icon={FileText}
              title="Invoice"
              description="Send a formal invoice for the booking amount"
              onClick={() => toast.info('Select a booking below to send invoice')}
              disabled={false}
            />
          </div>
        </CardContent>
      </Card>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Communication History</p>
          <p className="mt-1 text-blue-700">
            Communication history is logged in the audit system. To view sent emails, 
            check the global Email Analytics section or the booking's activity log when available.
          </p>
        </div>
      </div>

      {/* Booking Communications Table */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-violet-600" />
            Bookings
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search by name, email, booking ref..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Bookings</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="deposit_paid">Deposit Paid</SelectItem>
                <SelectItem value="fully_paid">Fully Paid</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Count */}
          <div className="text-sm text-slate-500 mb-3">
            {filteredBookings.length} of {activeBookings.length} bookings
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : activeBookings.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No bookings available</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-medium text-slate-600">BOOKING</th>
                      <th 
                        className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('customer')}
                      >
                        <span className="flex items-center">CUSTOMER <SortIcon field="customer" /></span>
                      </th>
                      <th 
                        className="text-left py-2 px-3 font-medium text-slate-600 cursor-pointer hover:text-slate-900"
                        onClick={() => handleSort('status')}
                      >
                        <span className="flex items-center">STATUS <SortIcon field="status" /></span>
                      </th>
                      <th className="text-center py-2 px-3 font-medium text-slate-600">PAYMENTS</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-600">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-slate-500">
                          No bookings match your filters
                        </td>
                      </tr>
                    ) : filteredBookings.map((booking) => {
                      const hasPayments = (booking.payment_records?.length || 0) > 0;
                      
                      return (
                        <tr 
                          key={booking.booking_id} 
                          className="border-b border-slate-100 hover:bg-slate-50"
                        >
                          <td className="py-3 px-3">
                            <span className="font-mono text-xs text-slate-600">
                              {booking.booking_id?.slice(0, 15)}...
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <div className="font-medium text-slate-900">{booking.customer_name}</div>
                            <div className="text-xs text-slate-500">{booking.customer_email}</div>
                          </td>
                          <td className="py-3 px-3">
                            <Badge className={cn("text-xs", statusColors[booking.status])}>
                              {statusLabels[booking.status] || booking.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className={cn(
                              "text-sm",
                              hasPayments ? "text-emerald-600 font-medium" : "text-slate-400"
                            )}>
                              {booking.payment_records?.length || 0}
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openConfirmDialog('confirmation', booking)}
                                title="Send Confirmation"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openConfirmDialog('receipt', booking)}
                                disabled={!hasPayments}
                                title={hasPayments ? "Send Receipt" : "No payments to receipt"}
                              >
                                <Receipt className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openConfirmDialog('invoice', booking)}
                                title="Send Invoice"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => onBookingSelect(booking)}
                                title="View Booking"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {filteredBookings.map((booking) => {
                  const hasPayments = (booking.payment_records?.length || 0) > 0;
                  
                  return (
                    <div 
                      key={booking.booking_id}
                      className="border border-slate-200 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-medium text-slate-900">{booking.customer_name}</p>
                          <p className="text-xs text-slate-500">{booking.customer_email}</p>
                          <p className="text-xs text-slate-400 font-mono mt-1">{booking.booking_id?.slice(0, 15)}...</p>
                        </div>
                        <Badge className={cn("text-xs", statusColors[booking.status])}>
                          {statusLabels[booking.status]}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                        <span className="text-sm text-slate-500">
                          {booking.payment_records?.length || 0} payment(s)
                        </span>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openConfirmDialog('confirmation', booking)}
                          >
                            <Send className="w-3 h-3 mr-1" />
                            Confirm
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openConfirmDialog('receipt', booking)}
                            disabled={!hasPayments}
                          >
                            <Receipt className="w-3 h-3 mr-1" />
                            Receipt
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog 
        open={confirmDialog.open} 
        onOpenChange={(open) => !open && setConfirmDialog({ open: false, type: null, booking: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog.type === 'confirmation' && 'Send Booking Confirmation'}
              {confirmDialog.type === 'receipt' && 'Send Payment Receipt'}
              {confirmDialog.type === 'invoice' && 'Send Invoice'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.type === 'confirmation' && (
                <>
                  This will send a booking confirmation email to <strong>{confirmDialog.booking?.customer_email}</strong>.
                  <br /><br />
                  Customer: <strong>{confirmDialog.booking?.customer_name}</strong>
                </>
              )}
              {confirmDialog.type === 'receipt' && (
                <>
                  This will send a payment receipt showing all recorded payments to <strong>{confirmDialog.booking?.customer_email}</strong>.
                  <br /><br />
                  Customer: <strong>{confirmDialog.booking?.customer_name}</strong>
                  <br />
                  Payments: <strong>{confirmDialog.booking?.payment_records?.length || 0}</strong>
                </>
              )}
              {confirmDialog.type === 'invoice' && (
                <>
                  This will send a formal invoice to <strong>{confirmDialog.booking?.customer_email}</strong>.
                  <br /><br />
                  Customer: <strong>{confirmDialog.booking?.customer_name}</strong>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={executeAction}
              disabled={sending}
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
